import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";

// A select with a short "most used" list, a "view more" toggle for the long tail,
// and a manual search box. Options: [{ value, common }]; labels resolved by `labelFor`.
export const SearchableSelect = ({
  value,
  onValueChange,
  options,
  labelFor,
  placeholder,
  searchPlaceholder,
  testid = "searchable-select",
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term) return options.filter((o) => labelFor(o.value).toLowerCase().includes(term));
    return showAll ? options : options.filter((o) => o.common);
  }, [q, showAll, options, labelFor]);

  const hasMore = !q.trim() && !showAll && options.some((o) => !o.common);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setQ(""); setShowAll(false); } }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <span className={value ? "" : "text-muted-foreground"}>{value ? labelFor(value) : placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            data-testid={`${testid}-search`}
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {filtered.length === 0 && (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">{t("marketplace.noResults")}</p>
          )}
          {filtered.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onValueChange(o.value); setOpen(false); setQ(""); setShowAll(false); }}
              data-testid={`${testid}-option-${o.value}`}
              className="flex w-full items-center justify-between rounded-sm px-3 py-2 text-left text-sm hover:bg-accent"
            >
              {labelFor(o.value)}
              {value === o.value && <Check className="h-4 w-4 text-brand" />}
            </button>
          ))}
          {hasMore && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              data-testid={`${testid}-view-more`}
              className="w-full rounded-sm px-3 py-2 text-center text-xs font-medium text-brand hover:bg-accent"
            >
              {t("restoration.viewMore")}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
